<template>
	<expand-list-modal
		ref="$modal"
		title="Привязка к контрагентам в 1C"
		placeholder="Название или экземпляр 1С"
		:filter="filter"
		@update:filter="filter = $event"
	>
		<template #summary>
			[Выручка <span :style="{ margin: '0 1px' }">/</span> Долг: {{ totalRevenue }}
			<span :style="{ margin: '0 1px' }">/</span>
			<span class="cp-expand__debt">{{ totalDebt }}</span>]
		</template>

		<div
			v-if="!filteredCounterparties.length"
			class="cp-expand__empty"
		>
			Ничего не найдено
		</div>

		<div
			v-for="(cp, index) in filteredCounterparties"
			:key="cp.Id"
			class="cp-expand__row"
		>
			<div class="cp-expand__row-left">
				<div class="cp-expand__row-count">{{ index + 1 }}.</div>
				<div class="cp-expand__row-name">
					{{ cp.Name }}
					<span :style="{ whiteSpace: 'nowrap' }">
						[{{ cp.Instance1C }}]
						<app-tooltip text="Подробная информация">
							<template #activator>
								<app-icon
									class="cp-expand__row-icon"
									:style="{ marginTop: '-2px' }"
									name="info-circle-outline"
									width="14px"
									color="#979797"
									hover-color="#499259"
									cursor="pointer"
									@click="openInfo(cp.Id)"
								/>
							</template>
						</app-tooltip>
						<app-resizable-dropdown
							v-if="canEdit"
							position="right"
							:close-by-click-outside="true"
							:ref="(el: any) => { if (el) $cpDetach[cp.Id] = el }"
						>
							<template #activator>
								<app-tooltip text="Удалить">
									<template #activator>
										<app-icon
											class="cp-expand__row-icon"
											:style="{ marginTop: '-2px' }"
											name="close-bold"
											width="12px"
											color="#D2D2D2"
											hover-color="#BA4C4C"
											cursor="pointer"
										/>
									</template>
								</app-tooltip>
							</template>
							<detach-counterparty-confirm-dropdown
								@cancel="$cpDetach[cp.Id]?.close()"
								@confirm="onDetach(cp.Id)"
							/>
						</app-resizable-dropdown>
					</span>
				</div>
			</div>
			<div class="cp-expand__row-right">
				{{ cp.Revenue?.toLocaleString('ru-RU') }}<span :style="{ margin: '0 1px' }">/</span>
				<span class="cp-expand__debt">{{ cp.Debt?.toLocaleString('ru-RU') }}</span>
			</div>
		</div>
	</expand-list-modal>

	<counterparty-info-modal ref="$infoModal" />
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import ExpandListModal from "@/components/common/modal/ExpandListModal.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import DetachCounterpartyConfirmDropdown from "@/components/main/client-block-page/dropdowns/DetachCounterpartyConfirmDropdown.vue";
import CounterpartyInfoModal from "@/components/main/client-block-page/modals/CounterpartyInfoModal.vue";
import { clientPageStore } from "@/store/client-page";

const clientStore = clientPageStore()

const client = computed(() => clientStore.openedClient)
const canEdit = computed(() => client.value?.CounterpartiesAccess === 2)

const filter = ref('')

const filteredCounterparties = computed(() => {
	const list = client.value?.Counterparties ?? []
	const q = filter.value.trim().toLowerCase()
	if (!q) {
		return list
	}
	return list.filter((cp: any) =>
		`${cp.Name ?? ''} ${cp.Instance1C ?? ''}`.toLowerCase().includes(q)
	)
})

const totalRevenue = computed(() =>
	(client.value?.Counterparties ?? [])
		.reduce((acc: number, cur: any) => acc + (cur.Revenue ?? 0), 0)
		.toLocaleString('ru-RU')
)
const totalDebt = computed(() =>
	(client.value?.Counterparties ?? [])
		.reduce((acc: number, cur: any) => acc + (cur.Debt ?? 0), 0)
		.toLocaleString('ru-RU')
)

const $modal = ref<InstanceType<typeof ExpandListModal> | null>(null)
const $infoModal = ref<InstanceType<typeof CounterpartyInfoModal> | null>(null)
const $cpDetach = ref<Record<number, any>>({})

const onOpen = () => {
	filter.value = ''
	$modal.value?.onOpen()
}
const onClose = () => $modal.value?.onClose()

const openInfo = (counterpartyId: number) => {
	$infoModal.value?.onOpen(counterpartyId)
}

const onDetach = async (id: number) => {
	try {
		await clientStore.detachCounterparty(id)
	} catch (e) {
		console.error(e)
	} finally {
		$cpDetach.value[id]?.close()
	}
}

defineExpose({ onOpen, onClose })
</script>

<style scoped lang="scss">
.cp-expand {
	&__debt {
		color: #D05A4F;
	}

	&__empty {
		font-size: 14px;
		line-height: 18px;
		color: #979797;
		padding: 4px 0;
	}

	&__row {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 8px;

		&-left {
			display: flex;
			align-items: flex-start;
			font-size: 14px;
			line-height: 18px;
			gap: 2px;
		}

		&-count {
			margin: 0 2px 0 3px;
			color: #4D4D4D;
		}

		&-name {
			color: #4D4D4D;
		}

		&-right {
			color: #4D4D4D;
			white-space: nowrap;
		}
	}
}
</style>
